import React, { useEffect, useState, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import html2pdf from "html2pdf.js";
import {
  User,
  Phone,
  MonitorSmartphone,
  Filter,
  CalendarClock,
  CalendarCheck2,
} from "lucide-react";
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

export default function SIPLogsPage() {
  const [logs, setLogs] = useState({});
  const [expanded, setExpanded] = useState({});
  const [traces, setTraces] = useState({});
  const [showExportDropdown, setShowExportDropdown] = useState({});
  const [loading, setLoading] = useState(true);

  // NOTE: showRegisterOnly = false by default (unchecked)
  const [filters, setFilters] = useState({
    from_user: "",
    to_user: "",
    user_agent: "",
    method: "",
    start_time: "",
    end_time: "",
    showRegisterOnly: false,
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const traceRefs = useRef({});

  // Helper: detect if a log is a REGISTER log
  const isRegisterLog = (log) => {
    const m = (log?.method || "").toUpperCase();
    if (m === "REGISTER") return true;

    // Look into headers/cseq if method is empty (common in responses)
    const cseq = (log?.cseq || log?.headers?.["CSeq"] || "").toUpperCase();
    if (cseq.includes("REGISTER")) return true;

    // Raw message pattern (defensive)
    const raw = (log?.raw_message || "").toUpperCase();
    if (raw.startsWith("REGISTER ")) return true;

    return false;
    };

  // Build API query (append method=REGISTER only when showRegisterOnly is true)
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });

    Object.entries(filters).forEach(([key, val]) => {
      if (key === "showRegisterOnly") return; // handled below
      if (val) params.append(key, val);
    });

    if (filters.showRegisterOnly) {
      // Nudge API to return just REGISTER transactions if it supports method filter
      // (we still filter client-side as the source of truth)
      if (!params.has("method")) params.append("method", "REGISTER");
    }

    return params.toString();
  }, [filters, page]);

  // Group logs by Call-ID
  const groupByCallId = (rows) => {
    return rows.reduce((acc, log) => {
      log.headers = log.headers || {};
      if (!acc[log.call_id]) acc[log.call_id] = [];
      acc[log.call_id].push(log);
      return acc;
    }, {});
  };

  // Apply REGISTER-only / REGISTER-hide filtering on grouped data
  const applyRegisterFilterToGroups = (grouped, showRegisterOnly) => {
    const entries = Object.entries(grouped);
    const filteredEntries = entries.filter(([_, group]) => {
      const hasRegister = group.some(isRegisterLog);
      return showRegisterOnly ? hasRegister : !hasRegister;
    });
    return Object.fromEntries(filteredEntries);
  };

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");

    fetch(`${API_BASE_URL}/voice/sip-logs?${buildQuery()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Bad response");
        }
        return res.json();
      })
      .then((data) => {
        const grouped = groupByCallId(data?.sip_logs || []);
        const filtered = applyRegisterFilterToGroups(grouped, filters.showRegisterOnly);

        setLogs(filtered || {});
        // Keep server pagination as-is (server returns total for all rows that matched
        // server-side filters; we’re additionally filtering for REGISTER client-side
        // when unchecked). If you want exact paging for REGISTER-hide, add an
        // exclude on the server and return the correct total.
        setTotalPages(Math.max(1, Math.ceil((data?.total || 0) / limit)));
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch SIP logs:", err);
        toast.error("Failed to load SIP logs");
        setLoading(false);
      });
  }, [API_BASE_URL, buildQuery, filters.showRegisterOnly]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === "checkbox" ? checked : value;
    setFilters((prev) => ({ ...prev, [name]: newVal }));
    setPage(1);
  };

  const toggleExpand = async (callId) => {
    setExpanded((prev) => ({ ...prev, [callId]: !prev[callId] }));
    if (!traces[callId]) {
      try {
        const token = localStorage.getItem("auth_token");
        const res = await fetch(`${API_BASE_URL}/voice/sip-trace/${callId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch trace");
        const text = await res.text();
        setTraces((prev) => ({ ...prev, [callId]: text }));
      } catch (err) {
        toast.error("Failed to fetch SIP trace");
        console.error(err);
      }
    }
  };

  const createStyledExportContent = (callId) => {
    const logGroup = Array.isArray(logs[callId]) ? logs[callId] : [];
    const latest = logGroup?.[0] || {};
    const { methodsWithStatus, allStatuses, allResponses } = getMethodsAndStatuses(logGroup || []);

    const styledContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>SIP Trace - ${callId}</title>
          <style>
            body { font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif; margin: 0; padding: 20px; background-color: #f9fafb; font-size: 14px; line-height: 1.5; }
            .trace-container { border-radius: 12px; border: 1px solid #e5e7eb; box-shadow: inset 0 2px 4px 0 rgb(0 0 0 / 0.05); overflow: hidden; background-color: white; max-height: none; }
            .trace-header { background-color: #f3f4f6; padding: 16px; border-bottom: 1px solid #e5e7eb; }
            .trace-header h1 { margin: 0; font-size: 18px; font-weight: 600; color: #374151; }
            .trace-header p { margin: 8px 0 0 0; color: #6b7280; font-size: 14px; }
            .summary-section { background-color: #ffffff; padding: 16px; border-bottom: 1px solid #e5e7eb; font-size: 12px; color: #374151; }
            .summary-title { font-weight: 600; color: #1f2937; margin-bottom: 8px; font-size: 14px; }
            .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 12px; }
            .summary-item span { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace; }
            .summary-label { color: #6b7280; font-weight: 500; }
            .headers-section { margin-top: 16px; }
            .headers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 8px; }
            .headers-grid strong { color: #6b7280; }
            .headers-grid span { font-family: ui-monospace, SFMono-Regular, monospace; }
            .badge { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; margin-right: 4px; margin-bottom: 4px; border: 1px solid; }
            .method-badge-invite { background-color: #f0fdf4; color: #166534; border-color: #bbf7d0; }
            .method-badge-bye { background-color: #fef2f2; color: #991b1b; border-color: #fecaca; }
            .method-badge-register { background-color: #eff6ff; color: #1e40af; border-color: #bfdbfe; }
            .method-badge-cancel { background-color: #fff7ed; color: #c2410c; border-color: #fed7aa; }
            .method-badge-options { background-color: #f3f4f6; color: #374151; border-color: #d1d5db; }
            .method-badge-ack { background-color: #fefce8; color: #a16207; border-color: #fde68a; }
            .method-badge-default { background-color: #faf5ff; color: #7c2d12; border-color: #e9d5ff; }
            .status-badge-1xx { background-color: #f3f4f6; color: #374151; }
            .status-badge-2xx { background-color: #dcfce7; color: #166534; }
            .status-badge-3xx { background-color: #dbeafe; color: #1e40af; }
            .status-badge-4xx { background-color: #fed7aa; color: #c2410c; }
            .status-badge-5xx { background-color: #fecaca; color: #991b1b; }
            .status-badge-default { background-color: #e9d5ff; color: #7c2d12; }
            .response-badge-ok { background-color: #dcfce7; color: #166534; }
            .response-badge-ringing { background-color: #dbeafe; color: #1e40af; }
            .response-badge-trying { background-color: #f3f4f6; color: #374151; }
            .response-badge-busy { background-color: #fed7aa; color: #c2410c; }
            .response-badge-error { background-color: #fecaca; color: #991b1b; }
            .response-badge-default { background-color: #e9d5ff; color: #7c2d12; }
            .message-flow { padding: 8px; }
            .message-item { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 8px; break-inside: avoid; }
            .message-header { padding: 8px 16px; font-size: 14px; font-weight: 500; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
            .message-header-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
            .message-header-right { font-size: 12px; color: #6b7280; font-family: ui-monospace, SFMono-Regular, monospace; }
            .timestamp { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 12px; color: #6b7280; }
            .message-type-request { background-color: #eff6ff; border-left: 4px solid #60a5fa; }
            .message-type-response { background-color: #f0fdf4; border-left: 4px solid #4ade80; }
            .type-badge-request { background-color: #dbeafe; color: #1e40af; }
            .type-badge-response { background-color: #dcfce7; color: #166534; }
            .raw-message { background-color: #1f2937; color: #f9fafb; padding: 16px; overflow-x: auto; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace; font-size: 12px; white-space: pre-wrap; margin: 0; }
            .additional-info { padding: 8px 16px; background-color: #f9fafb; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
            .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 8px; }
            .info-grid strong { color: #374151; }
            @media print { body { margin: 0; padding: 10px; } .trace-container { box-shadow: none; } .message-item { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="trace-container">
            <div class="trace-header">
              <h1>SIP Trace - ${callId}</h1>
              <p>Generated on ${new Date().toLocaleString()}</p>
            </div>

            <div class="summary-section">
              <h4 class="summary-title">Call Summary</h4>
              <div class="summary-grid">
                <div class="summary-item"><span class="summary-label">From:</span> <span>${latest?.from_user || 'N/A'}</span></div>
                <div class="summary-item"><span class="summary-label">To:</span> <span>${latest?.to_user || 'N/A'}</span></div>
                <div class="summary-item"><span class="summary-label">User Agent:</span> <span>${latest?.user_agent || 'N/A'}</span></div>
                <div class="summary-item"><span class="summary-label">Timestamp:</span> <span>${latest?.timestamp ? new Date(latest.timestamp).toLocaleString() : 'N/A'}</span></div>
              </div>

              <div style="margin-bottom: 8px;">
                <span class="summary-label">Methods: </span>
                ${
                  methodsWithStatus.map(({ method, statusCodes }) => {
                    const methodClass =
                      method.toLowerCase() === 'invite' ? 'method-badge-invite' :
                      method.toLowerCase() === 'bye' ? 'method-badge-bye' :
                      method.toLowerCase() === 'register' ? 'method-badge-register' :
                      method.toLowerCase() === 'cancel' ? 'method-badge-cancel' :
                      method.toLowerCase() === 'options' ? 'method-badge-options' :
                      method.toLowerCase() === 'ack' ? 'method-badge-ack' :
                      'method-badge-default';
                    const codesStr = (statusCodes && statusCodes.length > 0) ? (' ' + statusCodes.join(' ')) : '';
                    return '<span class="badge ' + methodClass + '">' + method + codesStr + '</span>';
                  }).join('')
                }
              </div>

              ${allStatuses.length > 0 ? `
              <div style="margin-bottom: 8px;">
                <span class="summary-label">Status Codes: </span>
                ${
                  allStatuses.map((status) => {
                    const code = parseInt(status, 10);
                    const statusClass =
                      code >= 100 && code < 200 ? 'status-badge-1xx' :
                      code >= 200 && code < 300 ? 'status-badge-2xx' :
                      code >= 300 && code < 400 ? 'status-badge-3xx' :
                      code >= 400 && code < 500 ? 'status-badge-4xx' :
                      code >= 500 && code < 600 ? 'status-badge-5xx' :
                      'status-badge-default';
                    return '<span class="badge ' + statusClass + '">' + status + '</span>';
                  }).join('')
                }
              </div>` : ''}

              ${allResponses.length > 0 ? `
              <div>
                <span class="summary-label">Response Phrases: </span>
                ${
                  allResponses.map((response) => {
                    const r = String(response).toLowerCase();
                    const responseClass =
                      r.includes('ok') ? 'response-badge-ok' :
                      r.includes('ringing') ? 'response-badge-ringing' :
                      r.includes('trying') ? 'response-badge-trying' :
                      r.includes('busy') ? 'response-badge-busy' :
                      (r.includes('error') || r.includes('failed')) ? 'response-badge-error' :
                      'response-badge-default';
                    return '<span class="badge ' + responseClass + '">' + response + '</span>';
                  }).join('')
                }
              </div>` : ''}

              ${
                latest?.headers && Object.keys(latest.headers).length > 0 ? `
                  <div class="headers-section">
                    <h4 class="summary-title">Headers</h4>
                    <div class="headers-grid">
                      ${
                        Object.entries(latest.headers).map(([key, value]) =>
                          '<div><strong>' + key + ':</strong> <span>' + String(value) + '</span></div>'
                        ).join('')
                      }
                    </div>
                  </div>
                ` : ''
              }
            </div>

            <div class="message-flow">
              ${
                logGroup.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((log) => {
                  const isRequest = log.message_type === 'request';
                  const statusCode = log.status_code;
                  const reasonPhrase = log.reason_phrase || '';
                  const method = log.method || '';

                  const getMethodBadgeClass = (m) => {
                    switch (m) {
                      case 'INVITE': return 'method-badge-invite';
                      case 'BYE': return 'method-badge-bye';
                      case 'REGISTER': return 'method-badge-register';
                      case 'CANCEL': return 'method-badge-cancel';
                      case 'OPTIONS': return 'method-badge-options';
                      case 'ACK': return 'method-badge-ack';
                      default: return 'method-badge-default';
                    }
                  };
                  const getStatusBadgeClass = (status) => {
                    const code = parseInt(status, 10);
                    if (code >= 100 && code < 200) return 'status-badge-1xx';
                    if (code >= 200 && code < 300) return 'status-badge-2xx';
                    if (code >= 300 && code < 400) return 'status-badge-3xx';
                    if (code >= 400 && code < 500) return 'status-badge-4xx';
                    if (code >= 500 && code < 600) return 'status-badge-5xx';
                    return 'status-badge-default';
                  };
                  const getResponseBadgeClass = (response) => {
                    const r = response.toLowerCase();
                    if (r.includes('ok')) return 'response-badge-ok';
                    if (r.includes('ringing')) return 'response-badge-ringing';
                    if (r.includes('trying')) return 'response-badge-trying';
                    if (r.includes('busy')) return 'response-badge-busy';
                    if (r.includes('error') || r.includes('failed')) return 'response-badge-error';
                    return 'response-badge-default';
                  };

                  return (
                    '<div class="message-item">' +
                      '<div class="message-header ' + (isRequest ? 'message-type-request' : 'message-type-response') + '">' +
                        '<div class="message-header-left">' +
                          '<span class="timestamp">' + new Date(log.timestamp).toLocaleTimeString() + '</span>' +
                          '<span class="badge ' + (isRequest ? 'type-badge-request' : 'type-badge-response') + '">' +
                            (isRequest ? 'REQUEST' : 'RESPONSE') +
                          '</span>' +
                          (method ? ('<span class="badge ' + getMethodBadgeClass(method) + '">' + method + '</span>') : '') +
                          (statusCode > 0 ? ('<span class="badge ' + getStatusBadgeClass(String(statusCode)) + '">' + statusCode + '</span>') : '') +
                          (reasonPhrase.trim() ? ('<span class="badge ' + getResponseBadgeClass(reasonPhrase) + '">' + reasonPhrase.trim() + '</span>') : '') +
                        '</div>' +
                        '<div class="message-header-right">' +
                          (log.source_ip || '') + ' → ' + (log.destination_ip || '') +
                        '</div>' +
                      '</div>' +
                      (log.raw_message ? ('<pre class="raw-message">' + log.raw_message + '</pre>') : '') +
                      '<div class="additional-info">' +
                        '<div class="info-grid">' +
                          (log.from_user ? ('<div><strong>From:</strong> ' + log.from_user + '</div>') : '') +
                          (log.to_user ? ('<div><strong>To:</strong> ' + log.to_user + '</div>') : '') +
                          (log.user_agent ? ('<div><strong>User Agent:</strong> ' + log.user_agent + '</div>') : '') +
                          (log.content_length > 0 ? ('<div><strong>Content Length:</strong> ' + log.content_length + '</div>') : '') +
                        '</div>' +
                      '</div>' +
                    '</div>'
                  );
                }).join('')
              }
            </div>
          </div>
        </body>
      </html>
    `;
    return styledContent;
  };

  const exportTrace = (callId, format = "pdf") => {
    const traceEl = traceRefs.current[callId];
    if (!traceEl || !traces[callId]) {
      toast.error("Trace not loaded yet.");
      return;
    }

    if (format === "html") {
      const styledContent = createStyledExportContent(callId);
      const blob = new Blob([styledContent], { type: "text/html" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `sip-trace-${callId}.html`;
      a.click();
      URL.revokeObjectURL(a.href);
    } else if (format === "pdf") {
      const styledContent = createStyledExportContent(callId);
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = styledContent;
      document.body.appendChild(tempDiv);

      const opt = {
        margin: 0.5,
        filename: `sip-trace-${callId}.pdf`,
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      };

      html2pdf()
        .set(opt)
        .from(tempDiv)
        .save()
        .then(() => {
          document.body.removeChild(tempDiv);
        })
        .catch((err) => {
          console.error("PDF export failed:", err);
          document.body.removeChild(tempDiv);
          toast.error("PDF export failed");
        });
    }
  };

  const getMethodsAndStatuses = (logGroup) => {
    const methodsWithStatus = new Map();
    const allStatuses = new Set();
    const allResponses = new Set();

    (logGroup || []).forEach((log) => {
      let method = log?.method;
      let statusCode = log?.status_code;
      let reasonPhrase = log?.reason_phrase;

      // Extract from message if available
      if (log?.message) {
        const msg = log.message;
        const mMatch = msg.match(/^(INVITE|BYE|REGISTER|CANCEL|OPTIONS|ACK|PRACK|UPDATE|REFER|SUBSCRIBE|NOTIFY|INFO|MESSAGE)/);
        if (mMatch) method = mMatch[1];
        const sMatch = msg.match(/SIP\/2\.0 (\d{3})/);
        if (sMatch) statusCode = Number(sMatch[1]);
      }

      if (typeof statusCode === "number" && statusCode > 0) {
        allStatuses.add(statusCode.toString());
      }

      if (reasonPhrase && String(reasonPhrase).trim() !== "") {
        allResponses.add(String(reasonPhrase).trim());
      }

      if (method) {
        if (!methodsWithStatus.has(method)) methodsWithStatus.set(method, new Set());
        if (typeof statusCode === "number" && statusCode > 0) {
          methodsWithStatus.get(method).add(statusCode.toString());
        }
      }
    });

    const methodsArray = Array.from(methodsWithStatus.entries()).map(([method, statusCodes]) => ({
      method,
      statusCodes: Array.from(statusCodes),
    }));

    return {
      methodsWithStatus: methodsArray,
      allStatuses: Array.from(allStatuses),
      allResponses: Array.from(allResponses),
    };
  };

  const getMethodBadgeColor = (method, hasStatus = false) => {
    const baseColor = (() => {
      switch (method) {
        case "INVITE": return hasStatus ? "bg-green-100 text-green-800 border-green-300" : "bg-green-50 text-green-600 border-green-200";
        case "BYE": return hasStatus ? "bg-red-100 text-red-800 border-red-300" : "bg-red-50 text-red-600 border-red-200";
        case "REGISTER": return hasStatus ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-blue-50 text-blue-600 border-blue-200";
        case "CANCEL": return hasStatus ? "bg-orange-100 text-orange-800 border-orange-300" : "bg-orange-50 text-orange-600 border-orange-200";
        case "OPTIONS": return hasStatus ? "bg-gray-100 text-gray-800 border-gray-300" : "bg-gray-50 text-gray-600 border-gray-200";
        case "ACK": return hasStatus ? "bg-yellow-100 text-yellow-800 border-yellow-300" : "bg-yellow-50 text-yellow-600 border-yellow-200";
        default: return hasStatus ? "bg-purple-100 text-purple-800 border-purple-300" : "bg-purple-50 text-purple-600 border-purple-200";
      }
    })();
    return `${baseColor} border`;
  };

  const getStatusBadgeColor = (status) => {
    const code = parseInt(status, 10);
    if (code >= 100 && code < 200) return "bg-gray-100 text-gray-800";
    if (code >= 200 && code < 300) return "bg-green-100 text-green-800";
    if (code >= 300 && code < 400) return "bg-blue-100 text-blue-800";
    if (code >= 400 && code < 500) return "bg-orange-100 text-orange-800";
    if (code >= 500 && code < 600) return "bg-red-100 text-red-800";
    return "bg-purple-100 text-purple-800";
  };

  const getResponseBadgeColor = (response) => {
    const r = String(response).toLowerCase();
    if (r.includes("ok")) return "bg-green-100 text-green-800";
    if (r.includes("ringing")) return "bg-blue-100 text-blue-800";
    if (r.includes("trying")) return "bg-gray-100 text-gray-800";
    if (r.includes("busy")) return "bg-orange-100 text-orange-800";
    if (r.includes("error") || r.includes("failed")) return "bg-red-100 text-red-800";
    return "bg-purple-100 text-purple-800";
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">📞 SIP Log Viewer</h2>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-5 mb-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="relative">
            <User className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
            <input
              name="from_user"
              placeholder="From User"
              value={filters.from_user}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>

          <div className="relative">
            <Phone className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
            <input
              name="to_user"
              placeholder="To User"
              value={filters.to_user}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>

          <div className="relative">
            <MonitorSmartphone className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
            <input
              name="user_agent"
              placeholder="User Agent"
              value={filters.user_agent}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
            <input
              name="method"
              placeholder="Method"
              value={filters.method}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>

          <div className="relative">
            <CalendarClock className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="datetime-local"
              name="start_time"
              value={filters.start_time}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>

          <div className="relative">
            <CalendarCheck2 className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="datetime-local"
              name="end_time"
              value={filters.end_time}
              onChange={handleFilterChange}
              className="pl-9 pr-3 py-2 border rounded w-full text-sm"
            />
          </div>
        </div>

        {/* REGISTER-only Toggle */}
        <div className="mt-4 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              name="showRegisterOnly"
              checked={filters.showRegisterOnly}
              onChange={handleFilterChange}
              className="w-4 h-4"
            />
            <span>Show REGISTER only</span>
          </label>
          {!filters.showRegisterOnly && (
            <span className="text-xs text-gray-500">
              REGISTER transactions are hidden by default.
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow border">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-600 border-b text-sm uppercase">
            <tr>
              <th className="px-4 py-3">Call ID</th>
              <th className="px-4 py-3">From</th>
              <th className="px-4 py-3">To</th>
              <th className="px-4 py-3">Methods</th>
              <th className="px-4 py-3">Status Codes</th>
              <th className="px-4 py-3">Response Phrases</th>
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {Object.entries(logs).length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-4 text-center text-gray-400">
                  {loading ? "Loading..." : "No SIP logs found"}
                </td>
              </tr>
            ) : (
              Object.entries(logs).map(([callId, logGroup]) => {
                const latest = logGroup[0];
                const { methodsWithStatus, allStatuses, allResponses } = getMethodsAndStatuses(logGroup);

                return (
                  <React.Fragment key={callId}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono">{callId}</td>
                      <td className="px-4 py-2">{latest?.from_user || ""}</td>
                      <td className="px-4 py-2">{latest?.to_user || ""}</td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {methodsWithStatus.map(({ method, statusCodes }) => (
                            <span
                              key={method}
                              className={`px-2 py-1 rounded text-xs font-medium ${getMethodBadgeColor(method, statusCodes.length > 0)}`}
                            >
                              {method}{statusCodes.length > 0 ? ` ${statusCodes.join(" ")}` : ""}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {allStatuses.map((status) => (
                            <span
                              key={status}
                              className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(status)}`}
                            >
                              {status}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1">
                          {allResponses.map((response) => (
                            <span
                              key={response}
                              className={`px-2 py-1 rounded text-xs font-medium ${getResponseBadgeColor(response)}`}
                            >
                              {response}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        {latest?.timestamp ? new Date(latest.timestamp).toLocaleString() : ""}
                      </td>
                      <td className="px-4 py-2 space-x-2 relative">
                        <button
                          onClick={() => toggleExpand(callId)}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          {expanded[callId] ? "Hide" : "View Trace"}
                        </button>

                        {expanded[callId] && traces[callId] && (
                          <div className="inline-block relative">
                            <button
                              onClick={() =>
                                setShowExportDropdown((prev) => ({
                                  ...prev,
                                  [callId]: !prev[callId],
                                }))
                              }
                              className="text-xs text-gray-600 hover:text-black px-2 py-1 border rounded shadow-sm bg-white ml-2"
                            >
                              Export ▼
                            </button>

                            {showExportDropdown[callId] && (
                              <div
                                className="absolute right-0 mt-2 w-24 bg-white rounded-md shadow-lg border z-20"
                                onMouseLeave={() =>
                                  setShowExportDropdown((prev) => ({
                                    ...prev,
                                    [callId]: false,
                                  }))
                                }
                              >
                                <button
                                  onClick={() => exportTrace(callId, "pdf")}
                                  className="w-full px-3 py-1 text-xs hover:bg-gray-100 text-left"
                                >
                                  PDF
                                </button>
                                <button
                                  onClick={() => exportTrace(callId, "html")}
                                  className="w-full px-3 py-1 text-xs hover:bg-gray-100 text-left"
                                >
                                  HTML
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    {expanded[callId] && traces[callId] && (
                      <tr>
                        <td colSpan={8} className="px-4 pb-6 bg-gray-50">
                          <div
                            ref={(el) => (traceRefs.current[callId] = el)}
                            className="rounded-xl border border-gray-200 shadow-inner overflow-hidden text-sm max-h-[600px] overflow-y-auto"
                          >
                            {/* Trace summary + full flow (already styled in export) */}
                            <div className="px-4 py-3 text-xs text-gray-600">
                              <div className="mb-2 font-semibold text-gray-800">Trace (structured + raw)</div>
                            </div>

                            <div className="space-y-2">
                              {logGroup
                                .slice()
                                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                                .map((log, idx) => {
                                  const isRequest = log.message_type === 'request';
                                  const statusCode = log.status_code;
                                  const reasonPhrase = log.reason_phrase;
                                  const method = log.method;

                                  return (
                                    <div key={idx} className="border rounded-lg overflow-hidden">
                                      <div className={`px-4 py-2 text-sm font-medium flex items-center justify-between ${
                                        isRequest ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-green-50 border-l-4 border-green-400'
                                      }`}>
                                        <div className="flex items-center space-x-3">
                                          <span className="font-mono text-xs text-gray-500">
                                            {new Date(log.timestamp).toLocaleTimeString()}
                                          </span>
                                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            isRequest ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                          }`}>
                                            {isRequest ? 'REQUEST' : 'RESPONSE'}
                                          </span>
                                          {method && (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodBadgeColor(method)}`}>
                                              {method}
                                            </span>
                                          )}
                                          {statusCode > 0 && (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeColor(statusCode.toString())}`}>
                                              {statusCode}
                                            </span>
                                          )}
                                          {reasonPhrase && reasonPhrase.trim() && (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${getResponseBadgeColor(reasonPhrase.trim())}`}>
                                              {reasonPhrase.trim()}
                                            </span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {log.source_ip} → {log.destination_ip}
                                        </div>
                                      </div>

                                      {log.raw_message && (
                                        <div className="bg-gray-900 text-gray-100 p-4 overflow-x-auto">
                                          <pre className="text-xs font-mono whitespace-pre-wrap">
                                            {log.raw_message}
                                          </pre>
                                        </div>
                                      )}

                                      <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 border-t">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                          {log.from_user && <div><strong>From:</strong> {log.from_user}</div>}
                                          {log.to_user && <div><strong>To:</strong> {log.to_user}</div>}
                                          {log.user_agent && <div><strong>User Agent:</strong> {log.user_agent}</div>}
                                          {log.content_length > 0 && <div><strong>Content Length:</strong> {log.content_length}</div>}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <button
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          className="px-4 py-2 border rounded disabled:opacity-50"
          disabled={page === 1}
        >
          ⬅ Previous
        </button>
        <span className="text-sm text-gray-600">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          className="px-4 py-2 border rounded disabled:opacity-50"
          disabled={page === totalPages}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
}
